import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import './SocialProofManager.css';

function SocialProofManager({ socialProof, onSocialProofChange }) {
    const [modalOpen, setModalOpen] = useState(false);
    const [editingProof, setEditingProof] = useState(null);
    const [formData, setFormData] = useState({
        source: 'google',
        strength: 7,
        
        // Review-basierte Felder
        reviewCount: '',
        averageRating: '',
        
        // Social Media Felder
        followerCount: '',
        postCount: '',
        engagementRate: '',
        
        // Presse & Content Felder
        articleCount: '',
        publicationNames: '',
        
        // Awards & Zertifikate
        awardName: '',
        certificationLevel: '',
        
        // Google Visibility Felder
        keywordRankings: '',
        monthlySearchVolume: '',
        domainAuthority: '',
        
        // Universal
        proofType: 'reviews', // reviews | followers | articles | awards | testimonials
        notes: ''
    });

    const availableSources = [
        { 
            id: 'google', 
            label: 'Google Reviews', 
            icon: 'fab fa-google', 
            color: '#4285F4',
            type: 'reviews',
            description: 'Bewertungen auf Google'
        },
        { 
            id: 'google-visibility', 
            label: 'Google Visibility', 
            icon: 'fas fa-search', 
            color: '#34A853',
            type: 'visibility',
            description: 'SEO & Google Rankings'
        },
        { 
            id: 'facebook', 
            label: 'Facebook', 
            icon: 'fab fa-facebook', 
            color: '#1877F2',
            type: 'mixed',
            description: 'Bewertungen & Community'
        },
        { 
            id: 'instagram', 
            label: 'Instagram', 
            icon: 'fab fa-instagram', 
            color: '#E4405F',
            type: 'followers',
            description: 'Follower & Engagement'
        },
        { 
            id: 'linkedin', 
            label: 'LinkedIn', 
            icon: 'fab fa-linkedin', 
            color: '#0A66C2',
            type: 'mixed',
            description: 'Empfehlungen & Follower'
        },
        { 
            id: 'trustpilot', 
            label: 'Trustpilot', 
            icon: 'fas fa-star', 
            color: '#00B67A',
            type: 'reviews',
            description: 'Verbraucherbewertungen'
        },
        { 
            id: 'youtube', 
            label: 'YouTube', 
            icon: 'fab fa-youtube', 
            color: '#FF0000',
            type: 'followers',
            description: 'Abonnenten & Views'
        },
        { 
            id: 'testimonials', 
            label: 'Kundenstimmen', 
            icon: 'fas fa-quote-right', 
            color: '#9B51E0',
            type: 'testimonials',
            description: 'Website Testimonials'
        },
        { 
            id: 'press', 
            label: 'Presse & Medien', 
            icon: 'fas fa-newspaper', 
            color: '#FF6B35',
            type: 'articles',
            description: 'Medienberichterstattung'
        },
        { 
            id: 'awards', 
            label: 'Awards & Zertifikate', 
            icon: 'fas fa-trophy', 
            color: '#FFD700',
            type: 'awards',
            description: 'Auszeichnungen & Zertifikate'
        },
        { 
            id: 'podcast', 
            label: 'Podcast Erwähnungen', 
            icon: 'fas fa-podcast', 
            color: '#8B5CF6',
            type: 'articles',
            description: 'Podcast Features'
        }
    ];

    const openAddModal = () => {
        setEditingProof(null);
        setFormData({
            source: 'google',
            strength: 7,
            reviewCount: '',
            averageRating: '',
            followerCount: '',
            postCount: '',
            engagementRate: '',
            articleCount: '',
            publicationNames: '',
            awardName: '',
            certificationLevel: '',
            keywordRankings: '',
            monthlySearchVolume: '',
            domainAuthority: '',
            proofType: 'reviews',
            notes: ''
        });
        setModalOpen(true);
    };

    const openEditModal = (proof) => {
        setEditingProof(proof.id);
        setFormData({
            source: proof.source || 'google',
            strength: proof.strength || 7,
            reviewCount: proof.reviewCount || '',
            averageRating: proof.averageRating || '',
            followerCount: proof.followerCount || '',
            postCount: proof.postCount || '',
            engagementRate: proof.engagementRate || '',
            articleCount: proof.articleCount || '',
            publicationNames: proof.publicationNames || '',
            awardName: proof.awardName || '',
            certificationLevel: proof.certificationLevel || '',
            keywordRankings: proof.keywordRankings || '',
            monthlySearchVolume: proof.monthlySearchVolume || '',
            domainAuthority: proof.domainAuthority || '',
            proofType: proof.proofType || 'reviews',
            notes: proof.notes || ''
        });
        setModalOpen(true);
    };

    const handleSave = () => {
        let newSocialProof;
        if (editingProof) {
            // Update existing
            newSocialProof = socialProof.map(p => 
                p.id === editingProof ? { ...p, ...formData } : p
            );
        } else {
            // Add new
            newSocialProof = [...socialProof, { id: Date.now(), ...formData }];
        }
        
        onSocialProofChange(newSocialProof);
        setModalOpen(false);
    };

    const handleRemove = (proofId) => {
        if (window.confirm('Möchten Sie diesen Social Proof wirklich entfernen?')) {
            onSocialProofChange(socialProof.filter(p => p.id !== proofId));
        }
    };

    const getSourceConfig = (sourceId) => {
        return availableSources.find(s => s.id === sourceId) || availableSources[0];
    };

    const getStrengthLabel = (strength) => {
        if (strength <= 3) return 'Wenig Einfluss';
        if (strength <= 5) return 'Moderat';
        if (strength <= 7) return 'Stark';
        if (strength <= 9) return 'Sehr stark';
        return 'Extrem stark';
    };

    const getSourceType = (sourceId) => {
        const source = availableSources.find(s => s.id === sourceId);
        return source?.type || 'reviews';
    };

    const shouldShowReviewFields = () => {
        const type = getSourceType(formData.source);
        return type === 'reviews' || type === 'mixed' || type === 'testimonials';
    };

    const shouldShowArticleFields = () => {
        const type = getSourceType(formData.source);
        return type === 'articles';
    };

    const shouldShowAwardFields = () => {
        const type = getSourceType(formData.source);
        return type === 'awards';
    };

    const shouldShowVisibilityFields = () => {
        const type = getSourceType(formData.source);
        return type === 'visibility';
    };

    const getProofSummary = (proof) => {
        const parts = [];
        const sourceType = getSourceType(proof.source);
        
        // Review-basierte Infos
        if (proof.reviewCount) parts.push(`${proof.reviewCount} Bewertungen`);
        if (proof.averageRating) parts.push(`⭐ ${proof.averageRating}/5.0`);
        
        // Artikel-basierte Infos
        if (proof.articleCount) parts.push(`${proof.articleCount} Artikel/Erwähnungen`);
        if (proof.publicationNames) parts.push(`in: ${proof.publicationNames}`);
        
        // Award-basierte Infos
        if (proof.awardName) parts.push(proof.awardName);
        if (proof.certificationLevel) parts.push(`Level: ${proof.certificationLevel}`);
        
        // Google Visibility Infos
        if (proof.keywordRankings) parts.push(`Keywords: ${proof.keywordRankings}`);
        if (proof.monthlySearchVolume) parts.push(`${proof.monthlySearchVolume} Suchen/Monat`);
        if (proof.domainAuthority) parts.push(`DA: ${proof.domainAuthority}`);
        
        return parts.length > 0 ? parts.join(' • ') : 'Keine weiteren Details angegeben';
    };

    return (
        <div className="social-proof-manager">
            {/* Existing Social Proof List */}
            {socialProof.length > 0 && (
                <div className="social-proof-manager-list">
                    {socialProof.map((proof) => {
                        const config = getSourceConfig(proof.source);
                        return (
                            <div key={proof.id} className="social-proof-manager-card">
                                <div className="social-proof-manager-card-header">
                                    <div className="social-proof-manager-card-title">
                                        <i className={config.icon} style={{ color: config.color }}></i>
                                        <h4>{config.label}</h4>
                                    </div>
                                    <div className="social-proof-manager-card-actions">
                                        <button
                                            className="social-proof-manager-edit-btn"
                                            onClick={() => openEditModal(proof)}
                                            title="Bearbeiten"
                                        >
                                            <i className="fas fa-edit"></i>
                                        </button>
                                        <button
                                            className="social-proof-manager-remove-btn"
                                            onClick={() => handleRemove(proof.id)}
                                            title="Löschen"
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                                <div className="social-proof-manager-card-details">
                                    <div className="social-proof-detail-item strength">
                                        <i className="fas fa-signal"></i>
                                        <span>{getStrengthLabel(proof.strength)}</span>
                                        <strong>{proof.strength}/10</strong>
                                    </div>
                                </div>
                                <div className="social-proof-manager-summary">
                                    <i className="fas fa-info-circle"></i>
                                    <span>{getProofSummary(proof)}</span>
                                </div>
                                {proof.notes && (
                                    <p className="social-proof-manager-card-notes">
                                        <i className="fas fa-sticky-note"></i>
                                        {proof.notes}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Add Button */}
            <button className="social-proof-manager-add-btn" onClick={openAddModal}>
                <i className="fas fa-plus-circle"></i>
                Social Proof hinzufügen
            </button>

            {/* Modal */}
            {modalOpen && ReactDOM.createPortal(
                <div className="social-proof-modal-overlay">
                    <div className="social-proof-modal">
                        <div className="social-proof-modal-header">
                            <h3>
                                <i className="fas fa-certificate"></i>
                                {editingProof ? 'Social Proof bearbeiten' : 'Neuer Social Proof'}
                            </h3>
                            <button className="social-proof-modal-close" onClick={() => setModalOpen(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <div className="social-proof-modal-body">
                            {/* Source Selection */}
                            <div className="social-proof-field">
                                <label>
                                    <i className="fas fa-source"></i>
                                    Quelle
                                </label>
                                <p className="social-proof-modal-subtitle">
                                    {getSourceConfig(formData.source).description}
                                </p>
                                <div className="social-proof-source-grid">
                                    {availableSources.map((source) => (
                                        <label
                                            key={source.id}
                                            className={`social-proof-source-option ${formData.source === source.id ? 'selected' : ''}`}
                                        >
                                            <input
                                                type="radio"
                                                name="source"
                                                value={source.id}
                                                checked={formData.source === source.id}
                                                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                                            />
                                            <div className="social-proof-source-card">
                                                <i className={source.icon} style={{ color: formData.source === source.id ? source.color : 'inherit' }}></i>
                                                <span>{source.label}</span>
                                                <small>{source.description}</small>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Strength Slider */}
                            <div className="social-proof-field">
                                <label>
                                    <i className="fas fa-signal"></i>
                                    Einfluss-Stärke
                                </label>
                                <p className="social-proof-modal-subtitle">Wie stark beeinflusst dies Kaufentscheidungen?</p>
                                <div className="social-proof-slider-container">
                                    <input
                                        type="range"
                                        min="1"
                                        max="10"
                                        value={formData.strength}
                                        onChange={(e) => setFormData({ ...formData, strength: parseInt(e.target.value) })}
                                        className="social-proof-slider"
                                    />
                                    <div className="social-proof-slider-display">
                                        <span className="social-proof-slider-value">{formData.strength}/10</span>
                                        <span className="social-proof-slider-label">{getStrengthLabel(formData.strength)}</span>
                                    </div>
                                </div>
                                {/* Quick Strength Buttons */}
                                <div className="social-proof-quick-strength">
                                    <button 
                                        type="button"
                                        className={`social-proof-quick-btn ${formData.strength <= 3 ? 'active' : ''}`}
                                        onClick={() => setFormData({ ...formData, strength: 3 })}
                                    >
                                        Gering
                                    </button>
                                    <button 
                                        type="button"
                                        className={`social-proof-quick-btn ${formData.strength > 3 && formData.strength <= 6 ? 'active' : ''}`}
                                        onClick={() => setFormData({ ...formData, strength: 5 })}
                                    >
                                        Mittel
                                    </button>
                                    <button 
                                        type="button"
                                        className={`social-proof-quick-btn ${formData.strength > 6 && formData.strength <= 8 ? 'active' : ''}`}
                                        onClick={() => setFormData({ ...formData, strength: 8 })}
                                    >
                                        Stark
                                    </button>
                                    <button 
                                        type="button"
                                        className={`social-proof-quick-btn ${formData.strength > 8 ? 'active' : ''}`}
                                        onClick={() => setFormData({ ...formData, strength: 10 })}
                                    >
                                        Extrem
                                    </button>
                                </div>
                            </div>

                            {/* Dynamic Fields based on Source Type */}
                            {(shouldShowReviewFields() || shouldShowArticleFields() || shouldShowAwardFields() || shouldShowVisibilityFields()) && (
                                <div className="social-proof-divider"></div>
                            )}

                            {/* Review Fields (Google, Trustpilot, Facebook, LinkedIn, Testimonials) */}
                            {shouldShowReviewFields() && (
                                <div className="social-proof-dynamic-section">
                                    <h4 className="social-proof-section-title">
                                        <i className="fas fa-star-half-alt"></i>
                                        Bewertungs-Details
                                    </h4>
                                    <div className="social-proof-row">
                                        <div className="social-proof-field">
                                            <label>
                                                <i className="fas fa-hashtag"></i>
                                                Anzahl Bewertungen
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.reviewCount}
                                                onChange={(e) => setFormData({ ...formData, reviewCount: e.target.value })}
                                                placeholder="z.B. 150"
                                                className="social-proof-input"
                                                min="0"
                                            />
                                        </div>
                                        <div className="social-proof-field">
                                            <label>
                                                <i className="fas fa-star"></i>
                                                Durchschnitt
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.averageRating}
                                                onChange={(e) => setFormData({ ...formData, averageRating: e.target.value })}
                                                placeholder="z.B. 4.8"
                                                className="social-proof-input"
                                                min="0"
                                                max="5"
                                                step="0.1"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Article Fields (Presse, Podcast) */}
                            {shouldShowArticleFields() && (
                                <div className="social-proof-dynamic-section">
                                    <h4 className="social-proof-section-title">
                                        <i className="fas fa-newspaper"></i>
                                        Medien-Details
                                    </h4>
                                    <div className="social-proof-row">
                                        <div className="social-proof-field">
                                            <label>
                                                <i className="fas fa-hashtag"></i>
                                                Anzahl Artikel/Erwähnungen
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.articleCount}
                                                onChange={(e) => setFormData({ ...formData, articleCount: e.target.value })}
                                                placeholder="z.B. 5"
                                                className="social-proof-input"
                                                min="0"
                                            />
                                        </div>
                                    </div>
                                    <div className="social-proof-field">
                                        <label>
                                            <i className="fas fa-building"></i>
                                            Publikationen/Plattformen
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.publicationNames}
                                            onChange={(e) => setFormData({ ...formData, publicationNames: e.target.value })}
                                            placeholder="z.B. Forbes, TechCrunch, Handelsblatt"
                                            className="social-proof-input"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Award Fields */}
                            {shouldShowAwardFields() && (
                                <div className="social-proof-dynamic-section">
                                    <h4 className="social-proof-section-title">
                                        <i className="fas fa-trophy"></i>
                                        Award-Details
                                    </h4>
                                    <div className="social-proof-field">
                                        <label>
                                            <i className="fas fa-award"></i>
                                            Name des Awards/Zertifikats
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.awardName}
                                            onChange={(e) => setFormData({ ...formData, awardName: e.target.value })}
                                            placeholder="z.B. Best Startup 2024, ISO 9001"
                                            className="social-proof-input"
                                        />
                                    </div>
                                    <div className="social-proof-field">
                                        <label>
                                            <i className="fas fa-layer-group"></i>
                                            Level/Kategorie
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.certificationLevel}
                                            onChange={(e) => setFormData({ ...formData, certificationLevel: e.target.value })}
                                            placeholder="z.B. Gold, Platin, International"
                                            className="social-proof-input"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Google Visibility Fields */}
                            {shouldShowVisibilityFields() && (
                                <div className="social-proof-dynamic-section">
                                    <h4 className="social-proof-section-title">
                                        <i className="fas fa-search"></i>
                                        SEO & Sichtbarkeits-Details
                                    </h4>
                                    <div className="social-proof-field">
                                        <label>
                                            <i className="fas fa-key"></i>
                                            Wichtigste Keywords
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.keywordRankings}
                                            onChange={(e) => setFormData({ ...formData, keywordRankings: e.target.value })}
                                            placeholder="z.B. Business Coach München, Online Marketing, ..."
                                            className="social-proof-input"
                                        />
                                    </div>
                                    <div className="social-proof-row">
                                        <div className="social-proof-field">
                                            <label>
                                                <i className="fas fa-chart-line"></i>
                                                Suchvolumen/Monat
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.monthlySearchVolume}
                                                onChange={(e) => setFormData({ ...formData, monthlySearchVolume: e.target.value })}
                                                placeholder="z.B. 10.000"
                                                className="social-proof-input"
                                            />
                                        </div>
                                        <div className="social-proof-field">
                                            <label>
                                                <i className="fas fa-globe"></i>
                                                Domain Authority
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.domainAuthority}
                                                onChange={(e) => setFormData({ ...formData, domainAuthority: e.target.value })}
                                                placeholder="z.B. 45"
                                                className="social-proof-input"
                                                min="0"
                                                max="100"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Notes */}
                            <div className="social-proof-field">
                                <label>
                                    <i className="fas fa-sticky-note"></i>
                                    Notizen (Optional)
                                </label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    placeholder="z.B. Besondere Erwähnungen, Links, etc."
                                    className="social-proof-textarea"
                                    rows="3"
                                />
                            </div>
                        </div>

                        <div className="social-proof-modal-footer">
                            <button className="social-proof-cancel-btn" onClick={() => setModalOpen(false)}>
                                Abbrechen
                            </button>
                            <button className="social-proof-save-btn" onClick={handleSave}>
                                <i className="fas fa-check"></i>
                                Speichern
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

export default SocialProofManager;

